const clioIntegrationContent = {
  title: "Clio Integration",
  description: "Connect Sortio to Clio Manage, match local files to the right matters (OCR included), and upload them with categories, clean names, and an audit trail.",
  sections: [
    {
      id: "overview",
      title: "What the Clio integration does",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          Sortio turns an unsorted local folder into filed Clio matter documents. Point it at a folder
          of intake scans, downloaded filings, or exported email attachments and it proposes a
          destination <strong>Clio matter</strong> for every file, each with a confidence score and a
          one-line rationale. You review the preview, override anything you disagree with, and only
          then does Sortio upload.
        </p>

        <div class="grid md:grid-cols-2 gap-4 my-8">
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2 flex items-center"><span class="mr-2">📄</span> Reads the documents, not just names</h4>
            <p class="text-[#424245]">Sortio can analyze file contents on your Mac, including OCR on scanned PDFs and images, so a file named "scan0043.pdf" still lands in the right matter. Content analysis is off until you turn it on, and Sortio offers it before matching.</p>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2 flex items-center"><span class="mr-2">🏷️</span> Categories and clean names</h4>
            <p class="text-[#424245]">Each upload can carry your firm's Clio document category (Pleading, Correspondence, Discovery…) and an AI-suggested professional document name. Your local file is never renamed.</p>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2 flex items-center"><span class="mr-2">✅</span> You approve every move</h4>
            <p class="text-[#424245]">Nothing reaches Clio until you confirm the preview. Low-confidence files default to Skip, and any suggestion can be overridden with a matter search.</p>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2 flex items-center"><span class="mr-2">🧾</span> Audit trail on every document</h4>
            <p class="text-[#424245]">Every uploaded document's description records its original filename, timestamp, match confidence, and Sortio version, so chain of custody survives the move.</p>
          </div>
        </div>

        <p class="text-[#1d1d1f] text-lg mb-6">
          The integration is available on <strong>Sortio Pro</strong> and <strong>Team</strong> plans, is listed in the
          <a href="https://www.clio.com/app-directory/sortio/" target="_blank" rel="noopener noreferrer" class="text-[#06c]">Clio App Directory</a>,
          and currently supports firms on Clio's <strong>United States</strong> region.
        </p>
      `
    },
    {
      id: "connect",
      title: "Connecting your Clio account",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          Connect once and Sortio keeps a local, read-only index of your open matters so matching is
          instant, even against thousands of matters.
        </p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden mb-6">
          <div class="p-6 space-y-4">
            <p class="text-[#424245]"><strong>1.</strong> In Sortio, open <strong>Integrations</strong> and click <strong>Connect</strong> on the Clio card.</p>
            <p class="text-[#424245]"><strong>2.</strong> Pick your Clio region (United States today; EU, Canada, and Australia are coming) and sign in to Clio in the browser window that opens.</p>
            <p class="text-[#424245]"><strong>3.</strong> Approve the requested permissions: Documents (read/write), Custom actions (read/write), Matters (read), Contacts (read), Users (read).</p>
            <p class="text-[#424245]"><strong>4.</strong> Sortio builds its local matter index. A firm with a few thousand open matters indexes in under a minute, and the index refreshes automatically.</p>
          </div>
        </div>

        <div class="border border-[#06c]/20 rounded-xl p-5 bg-[#06c]/5">
          <h4 class="font-semibold text-[#1d1d1f] flex items-center mb-2"><span class="mr-2">💡</span> Open in Sortio, from inside Clio</h4>
          <p class="text-[#424245]">After connecting, Sortio adds an <strong>"Open in Sortio"</strong> action to the matter page in Clio Manage. Click it on any matter and Sortio opens ready to file a folder of documents straight into that matter.</p>
        </div>
      `
    },
    {
      id: "sort-to-matter",
      title: "Filing a folder into matters",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          The main workflow is <strong>Sort a folder to Clio matters</strong>, available from the
          Integrations page and from the sort success screen once Clio is connected.
        </p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden mb-6">
          <div class="p-6 space-y-4">
            <p class="text-[#424245]"><strong>1. Pick a folder.</strong> Sortio scans it (up to 5,000 files per run) and checks how many files have analyzed content.</p>
            <p class="text-[#424245]"><strong>2. Analyze if needed.</strong> If most files haven't been read yet, Sortio offers to analyze them first, locally, with OCR for scans, so matching can use the client names, parties, and matter numbers inside the documents. You can also proceed on filenames alone.</p>
            <p class="text-[#424245]"><strong>3. Review the preview.</strong> Every file shows its proposed matter, confidence score, rationale, document category, and suggested document name. Confident matches are preselected; weak ones default to Skip until you assign a matter.</p>
            <p class="text-[#424245]"><strong>4. Upload.</strong> Files go directly from your Mac to Clio Documents under each matter, paced under Clio's API rate limit, through a queue that survives an app restart. Uploaded originals can optionally move into a dated "Sortio Uploaded" archive folder.</p>
          </div>
        </div>

        <p class="text-[#1d1d1f] text-lg mb-6">
          Re-running the flow on the same folder is safe: files already uploaded are recognized by
          content hash and skipped instead of duplicated.
        </p>
      `
    },
    {
      id: "privacy",
      title: "Privacy and data flow",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          Document contents and the files themselves <strong>never pass through Sortio's servers</strong>.
          Uploads go directly from your computer to Clio, and Sortio's servers never store your Clio
          tokens after the initial connection hand-off.
        </p>

        <p class="text-[#1d1d1f] text-lg mb-6">
          Classification sends only filenames, file types, and the party names Sortio extracted
          locally, alongside the candidate matter names it is choosing between, to Sortio's cloud
          classifier by default. Firms that prefer nothing leave the machine at all can switch to a
          local model (Ollama) or their own API key in <strong>Settings → AI Provider</strong>, which keeps matter
          and party names on-device too.
        </p>

        <div class="border border-[#06c]/20 rounded-xl p-5 bg-[#06c]/5">
          <h4 class="font-semibold text-[#1d1d1f] flex items-center mb-2"><span class="mr-2">🔒</span> Full details</h4>
          <p class="text-[#424245]">The exact data flow is documented in our <a href="/privacy-policy" class="text-[#06c]">Privacy Policy</a> (third-party integrations section) and in the <a href="/law-firms-confidentiality" class="text-[#06c]">confidentiality overview for law firms</a>.</p>
        </div>
      `
    },
    {
      id: "troubleshooting",
      title: "Troubleshooting",
      content: `
        <div class="space-y-4 mb-6">
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">"Clio is asking you to reconnect"</h4>
            <p class="text-[#424245]">Your Clio session was revoked or expired. Disconnect and reconnect from the Integrations page; the matter index rebuilds automatically.</p>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">A new matter isn't matching</h4>
            <p class="text-[#424245]">The index refreshes on launch and on connect. To pick up a matter opened minutes ago, reopen the app or reconnect; the matcher only considers open matters.</p>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">Lots of files land in "needs a matter"</h4>
            <p class="text-[#424245]">Two things help most. First, file under folders named for the client or matter: Sortio reads the folder chain, so a scan in "Clients/Smith v Jones/Discovery" matches even when its name is "Scan_0042.pdf". Second, turn on content analysis when Sortio offers it before matching; it then reads each document's subject line and the parties inside, which is what separates two matters for the same client. Files with nothing to go on are shown as "pick a matter" rather than given a guess.</p>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">Something else?</h4>
            <p class="text-[#424245]">Email <a href="mailto:marcus@getsortio.com" class="text-[#06c]">marcus@getsortio.com</a>. Include the approximate time and what you were doing; the local audit log at <code>~/Library/Application Support/Sortio/</code> makes issues quick to pin down.</p>
          </div>
        </div>
      `
    }
  ]
};

export default clioIntegrationContent;
